import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Home, BookOpen, Calendar, Users, Film, FileText, Heart, ArrowLeft, Play, Pause, PlayCircle, Clock, ChevronRight, Search, Sparkles, Library, RefreshCw, Filter } from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';
import PremiumFooter from './PremiumFooter';

function LorePage() {
  const navigate = useNavigate();
  const [userId] = useState(() => localStorage.getItem('workbook_user_id'));
  const [userName] = useState(() => localStorage.getItem('workbook_user_name'));
  const [privateLore, setPrivateLore] = useState(null);
  const [cohortLore, setCohortLore] = useState([]);
  const [globalLore, setGlobalLore] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [aiSummary, setAiSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(userId ? 'private' : 'global');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Calculate stats
  const totalEntries = globalLore.length + cohortLore.length + (privateLore ? 1 : 0);
  const totalVideos = videos.length;

  // Filter entries by search
  const filterEntries = (entries) => {
    if (!searchQuery) return entries;
    return entries.filter(entry => 
      entry.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.author?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredGlobalLore = filterEntries(globalLore);
  const filteredCohortLore = filterEntries(cohortLore);
  const filteredBlogPosts = filterEntries(blogPosts);

  const refreshAllData = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchGlobalLore(),
      fetchBlogPosts(),
      fetchVideos(),
      userId && fetchPrivateLore(),
      userId && fetchCohortLore()
    ].filter(Boolean));
    setRefreshing(false);
  };

  useEffect(() => {
    fetchGlobalLore();
    fetchBlogPosts();
    fetchVideos();
    if (userId) {
      fetchPrivateLore();
      fetchCohortLore();
    }
  }, [userId]);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos`);
      if (response.ok) {
        const data = await response.json();
        setVideos(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch session videos');
    }
  };

  const fetchPrivateLore = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lore/private/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setPrivateLore(data);
      }
    } catch (error) {
      console.error('Failed to fetch private LORE');
    }
  };

  const fetchCohortLore = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lore/cohort/2025-winter`);
      if (response.ok) {
        const data = await response.json();
        setCohortLore(data.lore_entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch cohort LORE');
    }
  };

  const fetchGlobalLore = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lore/global`);
      if (response.ok) {
        const data = await response.json();
        setGlobalLore(data.lore_entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch global LORE');
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lore/blog-posts`);
      if (response.ok) {
        const data = await response.json();
        setBlogPosts(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch blog posts');
    }
  };

  const generateAISummary = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lore/generate-summary/${userId}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setAiSummary(data.summary);
      }
    } catch (error) {
      console.error('Failed to generate AI summary');
    } finally {
      setLoading(false);
    }
  };

  // Theater-style entry detail view
  const renderEntryDetail = (entry) => (
    <div className="fixed inset-0 z-50 bg-black/95 overflow-auto">
      <div className="min-h-screen flex flex-col">
        {/* Theater Header */}
        <header className="sticky top-0 z-10 bg-gradient-to-b from-black to-transparent py-6 px-8">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <button 
              onClick={() => setSelectedEntry(null)}
              className="text-stone-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Library
            </button>
            {entry.featured && (
              <span className="text-amber-500 text-xs tracking-[0.2em] uppercase">Featured</span>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 px-8 pb-24">
          <article className="max-w-3xl mx-auto">
            {/* Title */}
            <header className="text-center mb-16 pt-8">
              <h1 className="text-4xl md:text-5xl font-light text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                {entry.title}
              </h1>
              <div className="flex items-center justify-center gap-4 text-stone-500 text-sm">
                <span>{entry.author}</span>
                <span className="w-1 h-1 rounded-full bg-stone-600"></span>
                <span>{new Date(entry.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </header>

            {/* Video if exists */}
            {entry.video_url && (
              <div className="mb-16">
                <video 
                  controls 
                  className="w-full aspect-video bg-stone-900 rounded"
                  poster=""
                >
                  <source src={entry.video_url} type="video/mp4" />
                </video>
              </div>
            )}

            {/* Content Body */}
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="text-stone-300 leading-[1.9] text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                <MarkdownRenderer content={entry.content} />
              </div>
            </div>

            {/* Resource Link */}
            {entry.file_url && (
              <div className="mt-16 pt-8 border-t border-stone-800">
                <a 
                  href={entry.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 text-amber-500 hover:text-amber-400 transition-colors"
                >
                  <FileText className="w-5 h-5" />
                  <span className="text-sm tracking-wide">View Attached Resource</span>
                </a>
              </div>
            )}
          </article>
        </main>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      
      {/* Selected Entry Modal */}
      {selectedEntry && renderEntryDetail(selectedEntry)}

      {/* Hero Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900 to-stone-950"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <div className="flex justify-between items-start mb-12">
            <button 
              onClick={() => navigate(userId ? '/dashboard' : '/')}
              className="text-stone-500 hover:text-white transition-colors flex items-center gap-2 text-sm"
              data-testid="lore-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {userId ? 'Dashboard' : 'Home'}
            </button>
            <div className="flex gap-3">
              <button 
                onClick={() => navigate('/wisdom')}
                className="text-stone-500 hover:text-amber-500 transition-colors text-sm"
              >
                Wisdom Library
              </button>
              <span className="text-stone-700">·</span>
              <button 
                onClick={() => navigate('/belonging-ebook')}
                className="text-stone-500 hover:text-amber-500 transition-colors text-sm"
              >
                Practice eBook
              </button>
            </div>
          </div>

          <div className="text-center">
            <p className="text-amber-500 text-xs tracking-[0.3em] uppercase mb-6">The Archive</p>
            <h1 className="text-5xl md:text-7xl font-light tracking-wide mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              LORE
            </h1>
            <p className="text-stone-400 text-lg font-light max-w-xl mx-auto leading-relaxed">
              Learning, Observations, Reflections, Evolution
            </p>
            <p className="text-stone-600 text-sm mt-4">
              A collection of wisdom from the journey in Nonviolent Communication
            </p>
            
            {/* Quick Stats */}
            <div className="flex justify-center gap-8 mt-8">
              <div className="text-center">
                <p className="text-2xl font-light text-white">{totalEntries}</p>
                <p className="text-stone-500 text-xs uppercase tracking-wider">Entries</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-light text-white">{totalVideos}</p>
                <p className="text-stone-500 text-xs uppercase tracking-wider">Videos</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-light text-white">{blogPosts.length}</p>
                <p className="text-stone-500 text-xs uppercase tracking-wider">Blog Posts</p>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="max-w-md mx-auto mt-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search wisdom, reflections, and insights..."
                  className="pl-12 bg-stone-900/50 border-stone-700 text-white placeholder:text-stone-500 h-12 rounded-full"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-500 hover:text-white"
                  >
                    ×
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-stone-500 text-sm mt-2">
                  Found {filteredGlobalLore.length + filteredCohortLore.length + filteredBlogPosts.length} results
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="sticky top-0 z-40 bg-stone-950/95 backdrop-blur-sm border-b border-stone-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-between items-center">
            <div className="flex justify-center gap-0 flex-1">
              {[
                { id: 'private', label: 'My Journey', icon: Heart, requiresAuth: true },
                { id: 'cohort', label: 'Our Cohort', icon: Users, requiresAuth: true },
                { id: 'global', label: 'Wisdom Library', icon: BookOpen, requiresAuth: false },
              ].map(tab => (
                (!tab.requiresAuth || userId) && (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-8 py-5 text-sm tracking-wide transition-all border-b-2 ${
                      activeTab === tab.id 
                        ? 'text-white border-amber-500' 
                        : 'text-stone-500 border-transparent hover:text-stone-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                )
              ))}
            </div>
            <button 
              onClick={refreshAllData}
              className="p-2 text-stone-500 hover:text-amber-500 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        
        {/* Private Journey */}
        {activeTab === 'private' && (
          <div className="space-y-16">
            <section className="text-center mb-16">
              <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {userName ? `${userName}'s Journey` : 'Your Journey'}
              </h2>
              <p className="text-stone-500">Personal reflections on growth in the Language of Life</p>
            </section>

            {/* Video Feature */}
            <section className="bg-stone-900/50 rounded-lg overflow-hidden">
              <div className="p-8 text-center">
                <p className="text-stone-500 text-xs tracking-[0.2em] uppercase mb-4">Foundation</p>
                <h3 className="text-2xl font-light text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  The Language of Belonging
                </h3>
                <video 
                  controls 
                  className="w-full max-w-3xl mx-auto aspect-video bg-black rounded"
                >
                  <source src="https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/yancj1kq_IMG_4343.mov" type="video/mp4" />
                </video>
              </div>
            </section>

            {/* AI Summary */}
            <section className="bg-gradient-to-b from-stone-900/30 to-transparent rounded-lg p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-amber-500 text-xs tracking-[0.2em] uppercase mb-2">Synthesis</p>
                  <h3 className="text-xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Journey Summary
                  </h3>
                </div>
                <Button
                  onClick={generateAISummary}
                  disabled={loading}
                  variant="outline"
                  className="border-stone-700 text-stone-300 hover:bg-stone-800 hover:text-white"
                >
                  {loading ? 'Generating...' : 'Generate'}
                </Button>
              </div>
              
              {aiSummary ? (
                <div className="prose prose-invert max-w-none">
                  <div className="text-stone-300 leading-relaxed border-l-2 border-amber-500/50 pl-6">
                    {aiSummary.split('\n').map((p, i) => (
                      <p key={i} className="mb-4 last:mb-0">{p}</p>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-stone-600">Generate a narrative summary of your learning journey</p>
                </div>
              )}
            </section>

            {/* Stats */}
            {privateLore && (
              <section className="grid md:grid-cols-2 gap-8">
                <div className="bg-stone-900/30 rounded-lg p-6">
                  <p className="text-stone-500 text-xs tracking-[0.2em] uppercase mb-4">Workbook</p>
                  <p className="text-4xl font-light text-white mb-2">{privateLore.total_responses}</p>
                  <p className="text-stone-600 text-sm">Responses recorded</p>
                </div>
                <div className="bg-stone-900/30 rounded-lg p-6">
                  <p className="text-stone-500 text-xs tracking-[0.2em] uppercase mb-4">Journal</p>
                  <p className="text-4xl font-light text-white mb-2">{privateLore.total_journal_entries}</p>
                  <p className="text-stone-600 text-sm">Entries written</p>
                </div>
              </section>
            )}
          </div>
        )}

        {/* Cohort - Private area for authenticated users */}
        {activeTab === 'cohort' && (
          <div className="space-y-12">
            <section className="text-center mb-16">
              <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Cohort
              </h2>
              <p className="text-stone-500">Session recordings and shared insights from our learning community</p>
            </section>

            {/* Session Recordings - Private to cohort members */}
            {videos.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <PlayCircle className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Session Recordings
                      </h3>
                      <p className="text-stone-500 text-sm">Watch recordings from our live sessions</p>
                    </div>
                  </div>
                  {videos.length > 3 && (
                    <button 
                      onClick={() => navigate('/dashboard')}
                      className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1"
                    >
                      View all in Dashboard <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Videos Grid */}
                <div className="grid md:grid-cols-3 gap-6">
                  {videos.slice(0, 6).map(video => (
                    <a 
                      key={video._id}
                      href={video.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden hover:border-purple-500/50 transition-all"
                    >
                      <div className="aspect-video relative">
                        <img 
                          src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-purple-500 rounded-full p-3">
                            <Play className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        {video.duration_minutes && (
                          <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {video.duration_minutes} min
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="text-white font-medium mb-1 line-clamp-2 group-hover:text-purple-300 transition-colors">
                          {video.title}
                        </h4>
                        <div className="flex items-center gap-2 text-stone-500 text-xs">
                          {video.session_number && (
                            <span className="bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                              Session {video.session_number}
                            </span>
                          )}
                          {video.session_date && (
                            <span>{new Date(video.session_date).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* Cohort Shared Content */}
            <section>
              <h3 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">Shared Insights</h3>
              {cohortLore.length === 0 && videos.length === 0 ? (
                <div className="text-center py-24">
                  <Users className="w-12 h-12 mx-auto mb-6 text-stone-700" />
                  <p className="text-stone-500">Coming soon</p>
                  <p className="text-stone-700 text-sm mt-2">Session highlights will appear here</p>
                </div>
              ) : cohortLore.length === 0 ? (
                <div className="text-center py-12 bg-stone-900/30 rounded-lg">
                  <p className="text-stone-600">No shared insights yet from your cohort</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {cohortLore.map(entry => (
                    <article 
                      key={entry.id}
                      onClick={() => setSelectedEntry(entry)}
                      className="group cursor-pointer bg-stone-900/30 hover:bg-stone-900/50 rounded-lg p-6 transition-all"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-lg font-light text-white group-hover:text-amber-400 transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {entry.title}
                        </h4>
                        {entry.featured && (
                          <span className="text-amber-500 text-[10px] tracking-[0.15em] uppercase">Featured</span>
                        )}
                      </div>
                      <p className="text-stone-500 text-sm line-clamp-3 mb-4">
                        {entry.content.substring(0, 150)}...
                      </p>
                      <p className="text-stone-700 text-xs">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </p>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* Global / Wisdom Library - Public area with Blog Posts */}
        {activeTab === 'global' && (
          <div className="space-y-12">
            <section className="text-center mb-16">
              <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Wisdom Library
              </h2>
              <p className="text-stone-500">Public blog posts and resources for the Language of Life</p>
            </section>

            {/* Published Blog Posts Section */}
            {blogPosts.length > 0 && (
              <section className="mb-12">
                <h3 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">Blog Posts</h3>
                <div className="space-y-6">
                  {blogPosts.map((post, idx) => (
                    <article 
                      key={post.id || post._id}
                      onClick={() => setSelectedEntry({
                        id: post.id || post._id,
                        title: post.title,
                        content: post.content,
                        author: post.author,
                        created_at: post.created_at,
                        video_url: post.video_url,
                        featured: post.featured
                      })}
                      className={`group cursor-pointer rounded-lg overflow-hidden transition-all ${
                        idx === 0 && post.featured 
                          ? 'bg-gradient-to-r from-amber-900/20 to-stone-900/30 hover:from-amber-900/30 hover:to-stone-900/40 p-8' 
                          : 'bg-stone-900/30 hover:bg-stone-900/50 p-6'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        {post.featured && (
                          <span className="text-amber-500 text-[10px] tracking-[0.15em] uppercase mb-2 block">Featured</span>
                        )}
                        {post.category && (
                          <span className="text-stone-600 text-[10px] tracking-wide uppercase">{post.category}</span>
                        )}
                      </div>
                      <h4 className={`font-light text-white group-hover:text-amber-400 transition-colors mb-3 ${
                        idx === 0 && post.featured ? 'text-2xl' : 'text-xl'
                      }`} style={{ fontFamily: 'Playfair Display, serif' }}>
                        {post.title}
                      </h4>
                      <p className="text-stone-500 text-sm mb-4 line-clamp-2">
                        {post.excerpt || (post.content ? post.content.substring(0, 200) + '...' : '')}
                      </p>
                      <div className="flex items-center gap-4 text-stone-600 text-xs">
                        <span>{post.author}</span>
                        <span className="w-1 h-1 rounded-full bg-stone-700"></span>
                        <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        {post.read_time && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-stone-700"></span>
                            <span>{post.read_time}</span>
                          </>
                        )}
                        {post.video_url && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-stone-700"></span>
                            <span className="flex items-center gap-1">
                              <Play className="w-3 h-3" /> Video
                            </span>
                          </>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* Global LORE Entries */}
            {globalLore.length > 0 && (
              <section>
                <h3 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">Resources & Articles</h3>
                <div className="space-y-6">
                  {globalLore.map((entry, idx) => (
                    <article 
                      key={entry.id}
                      onClick={() => setSelectedEntry(entry)}
                      className="group cursor-pointer bg-stone-900/30 hover:bg-stone-900/50 rounded-lg p-6 transition-all"
                    >
                      <div className="flex justify-between items-start mb-3">
                        {entry.featured && (
                          <span className="text-amber-500 text-[10px] tracking-[0.15em] uppercase mb-2 block">Featured</span>
                        )}
                      </div>
                      <h4 className="text-xl font-light text-white group-hover:text-amber-400 transition-colors mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {entry.title}
                      </h4>
                      <p className="text-stone-500 text-sm mb-4 line-clamp-2">
                        {entry.content.substring(0, 200)}...
                      </p>
                      <div className="flex items-center gap-4 text-stone-600 text-xs">
                        <span>{entry.author}</span>
                        <span className="w-1 h-1 rounded-full bg-stone-700"></span>
                        <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                        {entry.video_url && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-stone-700"></span>
                            <span className="flex items-center gap-1">
                              <Play className="w-3 h-3" /> Video
                            </span>
                          </>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {globalLore.length === 0 && blogPosts.length === 0 && (
              <div className="text-center py-24">
                <BookOpen className="w-12 h-12 mx-auto mb-6 text-stone-700" />
                <p className="text-stone-500">Library expanding</p>
                <p className="text-stone-700 text-sm mt-2">Curating the most valuable NVC resources</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Premium Footer */}
      <PremiumFooter variant="dark" />
    </div>
  );
}

export default LorePage;